import { useT } from "../../../shared/i18n";
import { Badge, Card } from "../../../shared/ui";
import type { ToolShape, TurnTrace } from "./inspectorApi";

const seconds = (ms: number): string => `${(ms / 1000).toFixed(1)} s`;

function toolText(tool: ToolShape): string {
  const keys = Object.entries(tool.arg_shape)
    .map(([key, type]) => `${key}: ${type}`)
    .join(", ");
  return `${tool.name}(${keys}) -> ${tool.status}`;
}

/** The per-turn timeline of one job: structure only, never message text. */
export function JobTrace({ turns }: { turns: TurnTrace[] }) {
  const t = useT();
  return (
    <ol className="flex flex-col gap-2" aria-label={t("admin.inspector.title")}>
      {turns.map((turn) => {
        const tokensIn = turn.model_calls.reduce(
          (sum, call) => sum + (call.tokens_in ?? 0),
          0,
        );
        const tokensOut = turn.model_calls.reduce(
          (sum, call) => sum + (call.tokens_out ?? 0),
          0,
        );
        const wall = turn.model_calls.reduce(
          (sum, call) => sum + call.wall_ms,
          0,
        );
        return (
          <li key={turn.n}>
            <Card>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-body font-semibold text-fg">
                  {t("admin.inspector.turn")} {turn.n}
                </span>
                <Badge tone="info">
                  {t("admin.inspector.intent")}{" "}
                  {turn.intent ?? t("admin.inspector.none")} (
                  {turn.intent_source})
                </Badge>
                {turn.corrections.length > 0 && (
                  <Badge tone="warning">
                    {t("admin.inspector.correction")}:{" "}
                    {turn.corrections.join(", ")}
                  </Badge>
                )}
              </div>
              <dl className="mt-2 grid gap-1 text-caption text-fg-muted">
                <div>
                  <dt className="inline font-semibold">
                    {t("admin.inspector.blocks")}:{" "}
                  </dt>
                  <dd className="inline">{turn.blocks.join("+")}</dd>
                </div>
                <div>
                  <dt className="inline font-semibold">
                    {t("admin.inspector.tools")}:{" "}
                  </dt>
                  <dd className="inline">
                    {turn.tools.length > 0
                      ? turn.tools.map(toolText).join("; ")
                      : t("admin.inspector.none")}
                  </dd>
                </div>
                <div>
                  {seconds(wall)} | {String(tokensIn)} in / {String(tokensOut)}{" "}
                  out | {String(turn.model_calls.length)} calls
                </div>
              </dl>
            </Card>
          </li>
        );
      })}
    </ol>
  );
}
